import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
  useMediaQuery,
  useTheme,
  Dialog,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationOnIcon,
  EmojiEvents as TrophyIcon,
  Upcoming as UpcomingIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Schedule as PlannedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrlWithFallback } from '../config/environment';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import registrationService from '../services/registrationService';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';

export default function Competitions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, getIdToken } = useFirebaseAuth();
  const [competitions, setCompetitions] = useState([]);
  const [competitionResults, setCompetitionResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed viewMode state since we're only using list view now
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const [clubIds, setClubIds] = useState([]);
  const [clubNames, setClubNames] = useState([]);
  const [interclubIds, setInterclubIds] = useState([]);
  const [currentSeasonIds, setCurrentSeasonIds] = useState([]);
  const [currentSeasonNames, setCurrentSeasonNames] = useState([]);
  const [clubFilterMode, setClubFilterMode] = useState('all');
  const [hasUnregisteredSkaters, setHasUnregisteredSkaters] = useState(false);
  const [seasonRegistrationOpen, setSeasonRegistrationOpen] = useState(false);
  const [skaterEligibility, setSkaterEligibility] = useState({}); // skaterId -> { name, attended: Set, planned: Set }

  useEffect(() => {
    loadCompetitions();
  }, []);

  // Load user's clubs and skaters (does NOT depend on seasons to avoid circular deps)
  const loadUserClubs = useCallback(async () => {
    if (!user) {
      setClubIds([]);
      setClubNames([]);
      setInterclubIds([]);
      setHasUnregisteredSkaters(false);
      return;
    }
    try {
      const token = await getIdToken();
      if (!token) return;
      const skatersResponse = await registrationService.getSkaters(token);
      const skatersList = skatersResponse.skaters || skatersResponse || [];

      const ids = new Set();
      const names = new Set();
      const interclubs = new Set();
      skatersList.forEach((skater) => {
        if (skater.clubId) ids.add(skater.clubId);
        if (skater.club) names.add(skater.club);
        if (skater.interclubId) interclubs.add(skater.interclubId);
      });
      const idList = Array.from(ids);
      const nameList = Array.from(names);
      const interclubList = Array.from(interclubs);
      setClubIds(idList);
      setClubNames(nameList);
      setInterclubIds(interclubList);
      if (idList.length === 0 && nameList.length === 0) {
        setClubFilterMode('all');
      }
    } catch (err) {
      console.error('Error loading user clubs:', err);
    }
  }, [user, getIdToken]);

  // Load user clubs once on user change
  useEffect(() => {
    loadUserClubs();
  }, [loadUserClubs]);

  // Load eligibility data for all user's skaters
  const loadSkaterEligibility = useCallback(async () => {
    if (!user) {
      setSkaterEligibility({});
      return;
    }
    try {
      const token = await getIdToken();
      if (!token) return;
      
      const skatersResponse = await registrationService.getSkaters(token);
      const skatersList = skatersResponse.skaters || skatersResponse || [];
      
      if (skatersList.length === 0) {
        setSkaterEligibility({});
        return;
      }

      const baseUrl = await getApiBaseUrlWithFallback();
      const eligibilityData = {};

      // Fetch eligibility for each skater
      await Promise.all(
        skatersList.map(async (skater) => {
          try {
            const response = await fetch(`${baseUrl}/skaters/${skater.id}/eligibility`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const data = await response.json();
              const attendedCompIds = new Set(
                (data.competitionsAttended || []).map((c) => c.competitionId)
              );
              const plannedCompIds = new Set(data.plannedEvents || []);
              eligibilityData[skater.id] = {
                name: `${skater.firstName} ${skater.lastName}`,
                firstName: skater.firstName,
                attended: attendedCompIds,
                planned: plannedCompIds,
              };
            }
          } catch (err) {
            console.error(`Error loading eligibility for skater ${skater.id}:`, err);
          }
        })
      );

      setSkaterEligibility(eligibilityData);
    } catch (err) {
      console.error('Error loading skater eligibility:', err);
    }
  }, [user, getIdToken]);

  // Load skater eligibility when user changes
  useEffect(() => {
    loadSkaterEligibility();
  }, [loadSkaterEligibility]);

  // Helper to get skater attendance info for a competition
  const getSkaterStatusForCompetition = useCallback((competitionId) => {
    const attended = [];
    const planned = [];
    
    Object.entries(skaterEligibility).forEach(([skaterId, data]) => {
      if (data.attended.has(competitionId)) {
        attended.push({ id: skaterId, name: data.name, firstName: data.firstName });
      } else if (data.planned.has(competitionId)) {
        planned.push({ id: skaterId, name: data.name, firstName: data.firstName });
      }
    });
    
    return { attended, planned };
  }, [skaterEligibility]);

  // Load seasons (depends on interclubIds)
  const loadCurrentSeasons = useCallback(async () => {
    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(`${baseUrl}/seasons?current=true`, {
        method: 'GET',
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch seasons');
      }
      const seasons = await response.json();
      let currentSeasons = seasons.filter((season) => season.isCurrent);
      if (currentSeasons.length === 0) {
        const fallbackResponse = await fetch(`${baseUrl}/seasons`, {
          method: 'GET',
          mode: 'cors'
        });
        if (fallbackResponse.ok) {
          const fallbackSeasons = await fallbackResponse.json();
          currentSeasons = fallbackSeasons.filter((season) => season.isCurrent);
        }
      }
      const scopedSeasons = interclubIds.length
        ? currentSeasons.filter((season) =>
            interclubIds.includes(season.interclubId) ||
            (Array.isArray(season.interclubIds) && season.interclubIds.some((id) => interclubIds.includes(id)))
          )
        : currentSeasons;
      setCurrentSeasonIds(scopedSeasons.map((season) => season.id));
      setCurrentSeasonNames(scopedSeasons.map((season) => season.name).filter(Boolean));
      setSeasonRegistrationOpen(scopedSeasons.some((season) => isSeasonRegistrationOpen(season)));
    } catch (err) {
      console.error('Error loading seasons:', err);
    }
  }, [interclubIds]);

  // Load seasons when interclubIds are available
  useEffect(() => {
    loadCurrentSeasons();
  }, [loadCurrentSeasons]);

  // Check registration status separately (runs after seasons are loaded)
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      if (!user || currentSeasonIds.length === 0) {
        return;
      }
      try {
        const token = await getIdToken();
        if (!token) return;
        const skatersResponse = await registrationService.getSkaters(token);
        const skatersList = skatersResponse.skaters || skatersResponse || [];
        
        if (skatersList.length === 0) {
          setHasUnregisteredSkaters(false);
          return;
        }
        
        const skaterIds = skatersList.map((s) => s.id).filter(Boolean);
        const registrations = await registrationService.getSeasonRegistrations(currentSeasonIds[0], token, skaterIds);
        const registeredIds = new Set(registrations.map((r) => r.skaterId));
        const unregistered = skatersList.some((s) => !registeredIds.has(s.id));
        setHasUnregisteredSkaters(unregistered);
      } catch (err) {
        console.error('Error checking registration status:', err);
      }
    };
    
    checkRegistrationStatus();
  }, [user, currentSeasonIds, getIdToken]);

  const loadCompetitions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the appropriate API base URL with fallback
      const baseUrl = await getApiBaseUrlWithFallback();

      // Fetch competitions and results in parallel
      const [competitionsResponse, resultsResponse] = await Promise.all([
        fetch(`${baseUrl}/competitions`, {
          method: 'GET',
          mode: 'cors'
        }).then(res => res.json()),
        fetch(`${baseUrl}/competition-results`, {
          method: 'GET',
          mode: 'cors'
        }).then(res => res.json())
      ]);

      setCompetitions(competitionsResponse);

      // Create a map of competition ID to results
      const resultsMap = {};
      resultsResponse.forEach(result => {
        if (!resultsMap[result.competitionId]) {
          resultsMap[result.competitionId] = [];
        }
        resultsMap[result.competitionId].push(result);
      });
      setCompetitionResults(resultsMap);

    } catch (err) {
      setError('Failed to load competitions');
      console.error('Error loading competitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCompetitionStatus = (competitionId) => {
    const results = competitionResults[competitionId];

    // Check if there are any LATEST competition results (not future/inactive ones)
    if (results && results.length > 0) {
      const hasLatestResult = results.some(r => r.isLatest === true);
      if (hasLatestResult) {
        return { status: 'complete', label: 'Processed', color: 'success', icon: <CheckCircleIcon /> };
      }
    }

    // No latest results uploaded yet (includes future competitions)
    return { status: 'pending', label: 'No Results', color: 'default', icon: <PendingIcon /> };
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return 'TBD';

    if (dateObj._seconds !== undefined) {
      // Firestore Timestamp - extract UTC date parts to avoid timezone shift
      const utcDate = new Date(dateObj._seconds * 1000);
      const date = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else if (dateObj instanceof Date) {
      // JavaScript Date
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else if (typeof dateObj === 'string') {
      // String date - parse without timezone conversion
      // If it's an ISO date string (YYYY-MM-DD), parse components directly
      const isoMatch = dateObj.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        // Create date using local timezone by specifying components
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
      // Fallback for other string formats
      return new Date(dateObj).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else {
      return 'TBD';
    }
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (start === end || !endDate) {
      return start;
    }
    return `${start} - ${end}`;
  };

  const getDateValue = (dateObj) => {
    if (!dateObj) return null;
    if (dateObj._seconds !== undefined) {
      // Firestore Timestamp - extract UTC date parts to avoid timezone shift
      const utcDate = new Date(dateObj._seconds * 1000);
      return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    }
    if (dateObj instanceof Date) {
      return dateObj;
    }
    // String date - parse without timezone issues
    if (typeof dateObj === 'string') {
      const isoMatch = dateObj.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    return new Date(dateObj);
  };

  const handleViewDetails = (competitionId) => {
    const results = competitionResults[competitionId];
    if (results && results.length > 0) {
      const latestResult = results.find(r => r.isLatest) || results[0];
      setSelectedCompetition({ competition: competitions.find(c => c.id === competitionId), result: latestResult });
      setDetailPopupOpen(true);
    } else {
      setSelectedCompetition({ competition: competitions.find(c => c.id === competitionId), result: null });
      setDetailPopupOpen(true);
    }
  };

  const handleCloseDetailPopup = () => {
    setDetailPopupOpen(false);
    setSelectedCompetition(null);
  };

  // Get all planned competition IDs from skater eligibility data
  const plannedCompetitionIds = useMemo(() => {
    const ids = new Set();
    Object.values(skaterEligibility).forEach((data) => {
      data.planned.forEach((compId) => ids.add(compId));
    });
    return ids;
  }, [skaterEligibility]);

  const filteredCompetitions = useMemo(() => {
    let baseCompetitions = competitions;
    if (currentSeasonIds.length > 0) {
      baseCompetitions = baseCompetitions.filter((competition) => {
        if (currentSeasonIds.includes(competition.seasonId)) return true;
        return competition.season?.isCurrent === true;
      });
    } else {
      baseCompetitions = baseCompetitions.filter((competition) => competition.season?.isCurrent !== false);
    }
    
    // Apply filter based on mode
    if (clubFilterMode === 'planned') {
      return baseCompetitions.filter((competition) => plannedCompetitionIds.has(competition.id));
    }
    if (clubFilterMode === 'club') {
      if (clubIds.length === 0 && clubNames.length === 0) return baseCompetitions;
      return baseCompetitions.filter((competition) => {
        if (competition.clubId && clubIds.includes(competition.clubId)) return true;
        if (competition.club && clubNames.includes(competition.club)) return true;
        return false;
      });
    }
    return baseCompetitions;
  }, [competitions, clubFilterMode, clubIds, clubNames, currentSeasonIds, plannedCompetitionIds]);

  const { upcomingCompetitions, pastCompetitions } = useMemo(() => {
    const now = new Date();
    const upcoming = [];
    const past = [];
    filteredCompetitions.forEach((competition) => {
      const endDateValue = getDateValue(competition.endDate) || getDateValue(competition.startDate || competition.date);
      if (endDateValue && endDateValue < now) {
        past.push(competition);
      } else {
        upcoming.push(competition);
      }
    });
    const getSortDate = (competition) =>
      getDateValue(competition.startDate || competition.date) ||
      getDateValue(competition.endDate);
    upcoming.sort((a, b) => {
      const aDate = getSortDate(a);
      const bDate = getSortDate(b);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    });
    past.sort((a, b) => {
      const aDate = getSortDate(a);
      const bDate = getSortDate(b);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return bDate - aDate;
    });
    return { upcomingCompetitions: upcoming, pastCompetitions: past };
  }, [filteredCompetitions]);

  // Mobile-friendly competition list item component
  const CompetitionListItem = ({ competition, onItemClick }) => {
    const status = getCompetitionStatus(competition.id);
    const results = competitionResults[competition.id];
    const hasLinks = Boolean(
      competition.liveScoreLink || competition.registrationLink || competition.clubWebsiteLink
    );
    const skaterStatus = getSkaterStatusForCompetition(competition.id);
    const hasSkaterActivity = skaterStatus.attended.length > 0 || skaterStatus.planned.length > 0;

    return (
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: hasSkaterActivity ? 'primary.main' : 'grey.200',
          backgroundColor: 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 3,
            transform: 'translateY(-2px)',
          }
        }}
        onClick={() => onItemClick(competition.id)}
      >
        {/* Status bar at top */}
        <Box sx={{ 
          bgcolor: status.color === 'success' ? 'success.main' : 
                  status.color === 'info' ? 'info.main' : 
                  status.color === 'warning' ? 'warning.main' : 'grey.600',
          py: 0.75,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
              ● {status.label}
            </Typography>
            {results && results.some((r) => r.isLatest) && (
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
                ✨ LATEST RESULTS
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {skaterStatus.attended.length > 0 && (
              <Chip
                size="small"
                icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                label={skaterStatus.attended.length === 1 
                  ? `${skaterStatus.attended[0].firstName} attended` 
                  : `${skaterStatus.attended.length} skaters attended`}
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem', 
                  fontWeight: 600,
                  bgcolor: 'white',
                  color: 'success.dark',
                  '& .MuiChip-icon': { color: 'success.dark' },
                }}
              />
            )}
            {skaterStatus.planned.length > 0 && (
              <Chip
                size="small"
                icon={<PlannedIcon sx={{ fontSize: '14px !important' }} />}
                label={skaterStatus.planned.length === 1 
                  ? `${skaterStatus.planned[0].firstName} planned` 
                  : `${skaterStatus.planned.length} skaters planned`}
                sx={{ 
                  height: 20, 
                  fontSize: '0.65rem', 
                  fontWeight: 600,
                  bgcolor: 'white',
                  color: 'info.dark',
                  '& .MuiChip-icon': { color: 'info.dark' },
                }}
              />
            )}
          </Box>
        </Box>
        
        {/* Main content */}
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            gap: 2,
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/competition.jpg`}
            alt="Competition badge"
            sx={{ 
              width: { xs: 56, sm: 64 }, 
              height: { xs: 56, sm: 64 }, 
              borderRadius: 2, 
              objectFit: 'cover',
              border: '2px solid',
              borderColor: 'grey.200'
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
              {competition.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDateRange(competition.startDate || competition.date, competition.endDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LocationOnIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {competition.location}
              </Typography>
            </Box>
            {results && results.length > 0 && (
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                📊 {results.find((r) => r.isLatest)?.resultsCount || results[0]?.resultsCount || 'Unknown'} results available
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            {competition.registrationLink && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 999,
                  minWidth: { xs: '100%', sm: 'auto' }
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(competition.registrationLink, '_blank', 'noopener,noreferrer');
                }}
              >
                Register
              </Button>
            )}
            {competition.clubWebsiteLink && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 999,
                  minWidth: { xs: '100%', sm: 'auto' }
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(competition.clubWebsiteLink, '_blank', 'noopener,noreferrer');
                }}
              >
                Club Site
              </Button>
            )}
            {competition.liveScoreLink && (
              <Button
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 999,
                  minWidth: { xs: '100%', sm: 'auto' }
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(competition.liveScoreLink, '_blank', 'noopener,noreferrer');
                }}
              >
                Live Scores
              </Button>
            )}
            {hasLinks ? null : (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Tap for details
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  // Competition detail popup component
  const CompetitionDetailPopup = ({ competition, result, open, onClose }) => {
    const status = getCompetitionStatus(competition.id);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/competition.jpg`}
              alt="Competition badge"
              sx={{ width: 64, height: 64, borderRadius: 2, objectFit: 'cover' }}
            />
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {competition.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {competition.club || competition.location || ''}
              </Typography>
            </Box>
            <Chip
              icon={status.icon}
              label={status.label}
              color={status.color}
              size="medium"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          {/* Basic info */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarIcon fontSize="medium" color="action" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatDateRange(competition.startDate || competition.date, competition.endDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon fontSize="medium" color="action" />
              <Typography variant="h6">
                {competition.location}
              </Typography>
            </Box>
            {competition.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                {competition.description}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Event links */}
          {(competition.liveScoreLink || competition.registrationLink || competition.clubWebsiteLink) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Event Links
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {competition.liveScoreLink && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                    href={competition.liveScoreLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Live Scores
                  </Button>
                )}
                {competition.registrationLink && (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                    href={competition.registrationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Registration
                  </Button>
                )}
                {competition.clubWebsiteLink && (
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                    href={competition.clubWebsiteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Club Website
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Results information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Results Status
            </Typography>

            {result ? (
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'info.50', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {result.source === 'live-scores'
                    ? 'Live score'
                    : result.source === 'pdf-upload'
                      ? 'File Uploaded'
                      : result.fileName
                        ? 'File Uploaded'
                        : 'Manually Entered'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Source
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  No results have been uploaded for this competition yet.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Close button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ minWidth: '120px' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadCompetitions} sx={{ ml: 2 }} size="small">
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, mb: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ 
            display: 'inline-flex', 
            p: 2, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mb: 2,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
          }}>
            <TrophyIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
            Series Competitions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View competition status and results for the current season
          </Typography>
          {currentSeasonNames.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Current season: {currentSeasonNames.join(', ')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {(clubIds.length > 0 || clubNames.length > 0) && (
            <Button
              variant={clubFilterMode === 'club' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setClubFilterMode('club')}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              My Club
            </Button>
          )}
          <Button
            variant={clubFilterMode === 'all' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setClubFilterMode('all')}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
          >
            All Clubs
          </Button>
          {plannedCompetitionIds.size > 0 && (
            <Button
              variant={clubFilterMode === 'planned' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setClubFilterMode('planned')}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              My Planned ({plannedCompetitionIds.size})
            </Button>
          )}
        </Box>
      </Paper>

      {/* Registration Warning Banner */}
      {user && hasUnregisteredSkaters && seasonRegistrationOpen && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="warning"
              size="small"
              variant="contained"
              onClick={() => navigate('/registration')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Register Now
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Season Registration Required
          </Typography>
          <Typography variant="body2">
            One or more of your skaters are not registered for the current season. Unregistered skaters are not eligible to compete.
          </Typography>
        </Alert>
      )}

      {/* Summary Statistics */}
      <Paper
        elevation={0}
        sx={{
          mb: { xs: 3, sm: 4 },
          p: { xs: 2, sm: 3 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.04) 100%)',
        }}
      >
        <Grid container spacing={isMobile ? 2 : 3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 1,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                <CalendarIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800 }}>
                {filteredCompetitions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Competitions
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                mb: 1,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
              }}>
                <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} color="success.main" sx={{ fontWeight: 800 }}>
                {filteredCompetitions.filter(comp => {
                  const results = competitionResults[comp.id];
                  return results && results.some(r => r.isLatest === true);
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processed
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ 
                display: 'inline-flex', 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                mb: 1,
                boxShadow: '0 4px 12px rgba(250, 112, 154, 0.3)'
              }}>
                <PendingIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 800 }}>
                {filteredCompetitions.filter(comp => {
                  const results = competitionResults[comp.id];
                  return !results || !results.some(r => r.isLatest === true);
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Results
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Competitions Display - Upcoming + Past */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
            }}>
              <UpcomingIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Upcoming Competitions
            </Typography>
          </Box>
          {upcomingCompetitions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {upcomingCompetitions.map((competition) => (
                <CompetitionListItem
                  key={competition.id}
                  competition={competition}
                  onItemClick={handleViewDetails}
                />
              ))}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No upcoming competitions found.
              </Typography>
            </Paper>
          )}
        </Box>

        <Divider />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <HistoryIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Past Competitions
            </Typography>
          </Box>
          {pastCompetitions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pastCompetitions.map((competition) => (
                <CompetitionListItem
                  key={competition.id}
                  competition={competition}
                  onItemClick={handleViewDetails}
                />
              ))}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No past competitions found.
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Footer Note */}
      <Paper
        elevation={0}
        sx={{
          mt: { xs: 3, sm: 4 },
          p: { xs: 2, sm: 3 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> This page shows the current status of all competitions.
          <br />
          • <strong>Processed:</strong> Competition results have been processed and are available
          <br />
          • <strong>Pending Results:</strong> Results have not been uploaded yet
          <br />
          Results are typically available within 24-48 hours after a competition concludes.
          If you don't see results for a competition you participated in, please contact your coach or club.
        </Typography>
      </Paper>

      {/* Competition Detail Popup */}
      {selectedCompetition && (
        <CompetitionDetailPopup
          competition={selectedCompetition.competition}
          result={selectedCompetition.result}
          open={detailPopupOpen}
          onClose={handleCloseDetailPopup}
        />
      )}
    </Box>
  );
}
